import { useMemo } from 'react';

import { Box } from 'components/Box';
import { H2 } from 'components/Text/H2';
import { HeadingSmall } from 'components/Text/HeadingSmall';
import { Text } from 'components/Text/Text';
import { CmsApiCtaHeroBlockData } from 'modules/cms/api/types/CmsApiCtaHero';
import { CmsApiPage } from 'modules/cms/api/types/CmsApiPage';
import { getContentPageUrl } from 'modules/cms/helpers';
import { useCurrentUserHasGradProgram, useIsLoggedIn } from 'modules/cms/hooks';
import { gutterWidth } from 'theme';
import { getStaticImageSrc } from 'utils/getImageSrc';

import { CmsAmpHeroBlockButton } from './CmsAmpHeroBlockButton';
import { getCmsAmpHeroBlockImageUrl } from './getCmsAmpHeroBlockImageUrl';

type AmpHeroBlockData = CmsApiCtaHeroBlockData & {
  isGrad?: boolean;
  isEmailSignup?: boolean;
  isMap?: boolean;
  label?: string;
};

type Props = {
  content: CmsApiPage;
  data: AmpHeroBlockData;
  blockId: string;
};

export function CmsAmpHeroBlock({
  content,
  data: {
    label,
    buttonText,
    buttonUrl,
    text,
    image,
    isGrad,
    isEmailSignup,
    isMap,
  },
  blockId,
}: Props) {
  const pageUrl = getContentPageUrl({ content });
  const loggedIn = useIsLoggedIn();
  const hasGradProgram = useCurrentUserHasGradProgram();

  const backgroundImageUrl = useMemo(() => {
    if (image && image.handle)
      return getCmsAmpHeroBlockImageUrl(image.handle, image.mimetype);

    if (isMap) return getStaticImageSrc('idealist-days-map.jpg');

    if (isEmailSignup) return getStaticImageSrc('blue-loops.svg');

    return getStaticImageSrc('blue-loops.svg');
  }, [image, isEmailSignup, isMap]);

  return (
    <Box
      height={450}
      mx={[-gutterWidth / 2, null, -gutterWidth]}
      overflow="hidden"
      color="white"
      textAlign="center"
      borderRadius={[0, null, null, 48]}
      backgroundImage={`url(${backgroundImageUrl})`}
      backgroundSize="cover"
    >
      <Box
        width={1}
        height="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        bg="rgba(0,0,0,0.35)"
        textAlign="center"
      >
        <Box maxWidth="768px" m="0 auto" px="20px">
          {label && <HeadingSmall>{label}</HeadingSmall>}
          {isMap ? (
            <Text
              dangerouslySetInnerHTML={{
                __html: text,
              }}
            />
          ) : (
            <H2>{text}</H2>
          )}
        </Box>

        <CmsAmpHeroBlockButton
          loggedIn={loggedIn}
          pageUrl={pageUrl}
          blockId={blockId}
          buttonUrl={buttonUrl || undefined}
          buttonText={buttonText || undefined}
          isGrad={Boolean(isGrad)}
          hasGradProgram={hasGradProgram}
        />
      </Box>
    </Box>
  );
}
